// Mobile sticky Add to Cart bar. See snippets/sticky-atc.liquid.
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.source = document.getElementById(this.dataset.buttonId);
        if (!this.source) return;

        this.priceEl = this.querySelector('[data-sticky-atc-price]');
        this.button = this.querySelector('[data-sticky-atc-button]');
        this.label = this.querySelector('[data-sticky-atc-label]');
        this.variantTitleEl = this.querySelector('[data-sticky-atc-variant-title]');
        this.variants = JSON.parse(this.querySelector('[data-sticky-atc-variants]')?.textContent || '{}');
        this.form = this.source.form;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.sourceVisible = true;

        this.hidden = false;
        this.sync();

        // global.js rewrites the inline button's text (price x quantity) and product-info.js
        // toggles its disabled state and may replace its children, so watch the whole button.
        this.observer = new MutationObserver(() => {
          this.sync();
          this.flushPendingAdd();
        });
        this.observer.observe(this.source, { attributes: true, childList: true, subtree: true, characterData: true });
        this.form?.addEventListener('change', () => this.sync());
        const qtyInput = document.getElementById(`Quantity-${this.dataset.sectionId}`);
        qtyInput?.addEventListener('input', () => this.sync());
        qtyInput?.addEventListener('change', () => this.sync());
        qtyInput?.closest('quantity-input')?.addEventListener('click', () => requestAnimationFrame(() => this.sync()));
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(() => this.sync()));
        }

        // A scroll check rather than IntersectionObserver: on this theme the observer never reports the
        // inline button leaving the viewport (tested on the preview at 390px). rAF keeps it to one read per frame.
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            // Shown whenever the inline button is off screen (below on arrival, or above after scrolling past),
            // hidden only while the inline button itself is in view so the two never show at once.
            const rect = this.source.getBoundingClientRect();
            this.sourceVisible = rect.bottom > 0 && rect.top < window.innerHeight;
            this.updateVisibility();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();

        this.onMediaChange = () => this.updateVisibility();
        this.mobileQuery.addEventListener('change', this.onMediaChange);

        this.button.addEventListener('click', () => this.onClick());
        this.querySelector('[data-sticky-atc-variant]')?.addEventListener('click', () => this.scrollToOptions());
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantUnsubscriber?.();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.mobileQuery?.removeEventListener('change', this.onMediaChange);
      }

      sync() {
        const variantId = (this.source.form || this.form)?.querySelector('input[name="id"]')?.value;
        const variant = this.variants[variantId];
        const soldOut = Boolean(variant) && variant.available === false;
        const unavailable = !variantId || !(variantId in this.variants);

        this.priceEl.textContent = this.formatPrice(this.unitPrice(variant) * this.quantity());
        if (this.variantTitleEl) this.variantTitleEl.textContent = variant ? variant.title : '';
        if (!this.pendingAdd) this.label.textContent = soldOut
          ? window.variantStrings?.soldOut || 'Sold Out'
          : window.variantStrings?.addToCart || 'Add to Cart';
        this.button.disabled = soldOut;
        this.unavailable = unavailable;
      }

      updateVisibility() {
        const show = this.mobileQuery.matches && !this.sourceVisible;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        this.button.tabIndex = show ? 0 : -1;
      }

      quantity() {
        const input = document.getElementById(`Quantity-${this.dataset.sectionId}`);
        return Math.max(1, parseInt(input?.value, 10) || 1);
      }

      unitPrice(variant) {
        // Wholesale customers: Samita shows its own price on the page; use that when present.
        const wholesale = document.querySelector(`#price-${this.dataset.sectionId} .samitaWS-customized-price`);
        const parsed = wholesale ? parseFloat(wholesale.textContent.replace(/[^\d.]/g, '')) : NaN;
        if (!Number.isNaN(parsed)) return parsed;
        return variant ? variant.price / 100 : 0;
      }

      formatPrice(amount) {
        // Same format as formatPrice() in global.js: "$165", "$1,790", "$168.83".
        const rounded = Math.round(amount * 100) / 100;
        const hasDecimal = rounded % 1 !== 0;
        return (
          (this.dataset.currency || '$') +
          rounded.toLocaleString('en-US', { minimumFractionDigits: hasDecimal ? 2 : 0, maximumFractionDigits: 2 })
        );
      }

      scrollToOptions() {
        const picker = document.querySelector(`#variant-selects-${this.dataset.sectionId}`) || this.source;
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      onClick() {
        if (this.button.disabled) return;
        if (this.unavailable) {
          // No valid variant selected: send the customer back to the options.
          this.scrollToOptions();
          return;
        }
        if (this.source.disabled) {
          // global.js (updatePriceByQty) disables the inline button for ~3s after every quantity change while it
          // redraws the price. Queue the tap and add as soon as it re-enables, rather than silently doing nothing.
          this.pendingAdd = true;
          this.button.setAttribute('aria-busy', 'true');
          this.label.textContent = 'Adding…';
          clearTimeout(this.pendingTimeout);
          this.pendingTimeout = setTimeout(() => this.cancelPendingAdd(), 6000);
          return;
        }
        // Same add-to-cart as the inline form (selected variant + quantity), which opens the cart drawer.
        this.source.click();
      }

      flushPendingAdd() {
        if (!this.pendingAdd || this.source.disabled) return;
        this.cancelPendingAdd();
        this.source.click();
      }

      cancelPendingAdd() {
        this.pendingAdd = false;
        clearTimeout(this.pendingTimeout);
        this.button.removeAttribute('aria-busy');
        this.sync();
      }
    }
  );
}
